import poppinsExtraLightLatinWoff2 from '@fontsource/poppins/files/poppins-latin-200-normal.woff2?url'
import { createFileRoute } from '@tanstack/react-router'
import { EN_FAQS } from '~/components/marketing/en/en-faq-section'
import { EnMarketingHomePage } from '~/components/marketing/en/en-home-page'
import { EN_PRICING } from '~/components/marketing/en/en-pricing-section'
import { alternateLinks, ogImageFor, ogImageMeta, ogLocaleMeta, SITE_URL } from '~/lib/site-meta'

const CANONICAL = '/en'

// SERP title — keyword-first, under ~60 chars to avoid truncation.
const PAGE_TITLE = 'Planno — practice management for architecture firms'
// Social title — poetic, used in OG/Twitter cards where length matters less.
const SOCIAL_TITLE = 'Planno — the whole practice in one place'
const HOME_DESCRIPTION =
  'The professional tool that brings an architecture practice’s projects, tasks, sprints, site visits, and clients together in one place.'
const KEYWORDS =
  'architecture practice management software, project management for architects, architecture firm software, AEC project management, site visit app, punch list software, construction administration, billable rate calculator, sprints for design teams'

export const Route = createFileRoute('/en/')({
  component: EnMarketingHomePage,
  head: () => ({
    links: [
      { href: `${SITE_URL}${CANONICAL}`, rel: 'canonical' },
      ...alternateLinks(CANONICAL),
      /* The hero's light half renders in Poppins ExtraLight above the fold;
         preload it here (not in __root) so app routes don't pay for a
         weight only this page uses. */
      {
        as: 'font',
        crossOrigin: 'anonymous',
        href: poppinsExtraLightLatinWoff2,
        rel: 'preload',
        type: 'font/woff2',
      },
    ],
    meta: [
      { title: PAGE_TITLE },
      { content: HOME_DESCRIPTION, name: 'description' },
      { content: KEYWORDS, name: 'keywords' },
      // Open Graph
      { content: 'website', property: 'og:type' },
      { content: 'Planno', property: 'og:site_name' },
      { content: SOCIAL_TITLE, property: 'og:title' },
      { content: HOME_DESCRIPTION, property: 'og:description' },
      { content: `${SITE_URL}${CANONICAL}`, property: 'og:url' },
      ...ogImageMeta('en'),
      ...ogLocaleMeta('en'),
      // Twitter / X
      { content: 'summary_large_image', name: 'twitter:card' },
      { content: SOCIAL_TITLE, name: 'twitter:title' },
      { content: HOME_DESCRIPTION, name: 'twitter:description' },
    ],
    scripts: [
      {
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          alternateName: 'Planno App',
          applicationCategory: 'BusinessApplication',
          applicationSubCategory: 'ProjectManagementApplication',
          audience: {
            '@type': 'BusinessAudience',
            audienceType: 'Architecture and engineering practices',
          },
          description: HOME_DESCRIPTION,
          featureList: [
            'Project management for architecture and engineering practices',
            'Tasks in weekly sprints and kanban',
            'Site visits with checklists and photos',
            'Project files with versioning',
            'Collaborative documents and specifications',
            'Client conversations attached to the project',
            'Team and contact management (clients, vendors, consultants)',
          ],
          image: ogImageFor('en'),
          inLanguage: 'en',
          keywords: KEYWORDS,
          name: 'Planno',
          offers: [
            {
              '@type': 'Offer',
              availability: 'https://schema.org/InStock',
              name: 'Personal',
              price: '0',
              priceCurrency: EN_PRICING.currency,
            },
            {
              '@type': 'Offer',
              availability: 'https://schema.org/InStock',
              category: 'Subscription',
              name: 'Professional',
              priceCurrency: EN_PRICING.currency,
              priceSpecification: {
                '@type': 'UnitPriceSpecification',
                price: EN_PRICING.proMonthly,
                priceCurrency: EN_PRICING.currency,
                referenceQuantity: {
                  '@type': 'QuantitativeValue',
                  unitCode: 'C62',
                  unitText: 'user',
                  value: '1',
                },
                unitCode: 'MON',
              },
            },
          ],
          operatingSystem: 'Web',
          provider: {
            '@type': 'Organization',
            name: 'Planno',
            url: SITE_URL,
          },
          url: `${SITE_URL}${CANONICAL}`,
        }),
        type: 'application/ld+json',
      },
      {
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          inLanguage: 'en',
          mainEntity: EN_FAQS.map((faq) => ({
            '@type': 'Question',
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.a,
            },
            name: faq.q,
          })),
        }),
        type: 'application/ld+json',
      },
      // No Organization / WebSite node here: those are site-wide singletons
      // already declared on `/` with a stable `@id`. Repeating them on a
      // second URL is how you end up with two competing entity definitions.
    ],
  }),
})
